import { useEffect } from 'react';
import $ from 'jquery';
import { __ } from '@wordpress/i18n';
import { useServiceResourcesQuery } from '../queries';
import { useBookingStore } from '../store/useBookingStore';
import {
	updateSelectOptions,
	updateRadioButtons,
} from '../utils/fieldRenderers';

/**
 * Hook to update resource field options when service changes.
 *
 * Reads all necessary data from the store, fetches new resources via React Query,
 * then updates both dropdowns and radio button groups.
 */
export function useResourceFieldUpdater(): void {
	const formId = useBookingStore((state) => state.formId);
	const currentServiceId = useBookingStore((state) => state.currentServiceId);
	const serviceFieldId = useBookingStore((state) => state.serviceFieldId);
	const resourceFieldIds = useBookingStore((state) => state.resourceFieldIds);

	// Fetch resources using React Query
	const { data: resources } = useServiceResourcesQuery(
		{ id: currentServiceId },
		{
			enabled:
				currentServiceId > 0 &&
				serviceFieldId !== null &&
				resourceFieldIds.length > 0,
		}
	);

	// Update resource fields when resources are fetched
	useEffect(() => {
		if (!resources || resourceFieldIds.length === 0) {
			return;
		}

		// Update each resource field
		resourceFieldIds.forEach((fieldId) => {
			/**
			 * Filter the resources used to populate an individual Resource field.
			 *
			 * @param {Array<Object>} resources         Resources available for the selected service.
			 * @param {Object}        context           Context for the Resource field being populated.
			 * @param {number}        context.formId    The current form ID.
			 * @param {number}        context.fieldId   The current Resource field ID.
			 * @param {number}        context.serviceId The selected service ID.
			 *
			 * @example Show only resources from a specific Resource Type
			 * <code>
			 * window.gform.addFilter('gpb_resource_field_resources', function(resources, context) {
			 *     return resources.filter(function(resource) {
			 *         return resource.resourceType === 123;
			 *     });
			 * });
			 * </code>
			 */
			const fieldResources = window.gform?.applyFilters
				? window.gform.applyFilters(
						'gpb_resource_field_resources',
						resources,
						{
							formId,
							fieldId,
							serviceId: currentServiceId,
						}
					)
				: resources;

			updateResourceField(formId, fieldId, fieldResources);
		});

		// Note: Pricing updates are handled by useGravityFormsPricing
		// which watches resource field changes via useDOMFieldSync
	}, [resources, formId, resourceFieldIds, currentServiceId]);
}

/**
 * Update a single resource field's options (select or radio).
 */
function updateResourceField(
	formId: number,
	fieldId: number,
	resources: Array<{ id: number; name: string }>
): void {
	const $field = $(`#field_${formId}_${fieldId}`);
	if (!$field.length) {
		return;
	}

	const $select = $field.find('select');
	const $radioContainer = $field.find('.gfield_radio');

	if ($select.length) {
		updateSelectOptions(
			$select,
			resources,
			__('Select a resource', 'gp-bookings')
		);
	} else if ($radioContainer.length) {
		updateRadioButtons($radioContainer, resources, formId, fieldId);
	}
}
